import {MouseEventHandler} from 'react';
import {useRouter} from 'next/router';
import {shallow} from 'zustand/shallow';
import {IconButton} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {AddIcon, RemoveIcon} from '@eksab/assets/icons';

import {PlayerItem} from './PlayerItem';
import {getPositionFromIndex, type State, getIndexFromPosition} from './usePlayersReducer';
import {useLineupStore} from './useLineupStore';
import {useSetQueryPlayerUIState} from './filters/usePlayersQuery';
import {useCompetitionQuery} from '../hooks/useCompetitionQuery';

type ListLineupProps = {hasStatsAction?: boolean; substitute?: boolean};

const subs = Array.from({length: 4});

export function ListLineup({hasStatsAction = false, ...props}: ListLineupProps) {
  const lineup = useLineupStore(
    (state) =>
      props.substitute ? null : state.formation.split('-').map((item) => Array.from({length: Number(item)}).fill(null)),
    (prevState, newState) =>
      prevState !== null &&
      newState !== null &&
      prevState[0].length === newState[0].length &&
      prevState[1].length === newState[1].length &&
      prevState[2].length === newState[2].length,
  ) as [Array<null>, Array<null>, Array<null>];

  return (
    <>
      {props.substitute ? (
        subs.map((_, index) => {
          const position = getPositionFromIndex(index as 0 | 1 | 2 | 3);

          return <ListPlayerItem key={index} index={index} position={position} isSubstitute />;
        })
      ) : (
        <>
          <ListPlayerItem key="gk" index={0} position="Goalkeeper" />

          {lineup[0].map((_, index) => (
            <ListPlayerItem key={`Def-${index}`} index={index} position="Defender" />
          ))}

          {lineup[1].map((_, index) => (
            <ListPlayerItem key={`Mid-${index}`} index={index} position="Midfielder" />
          ))}

          {lineup[2].map((_, index) => (
            <ListPlayerItem key={`Att-${index}`} index={index} position="Attacker" />
          ))}
        </>
      )}
    </>
  );
}

interface ListPlayerItemProps {
  position: State['indicatorPosition']['position'];
  index: number;
  isSubstitute?: boolean;
}

const ListPlayerItem = ({index, position, isSubstitute}: ListPlayerItemProps) => {
  const competitionId = useRouter().query.slug as string;

  const t = useTranslate();
  const player = useLineupStore((state) =>
    isSubstitute ? state.substitutes[index] : state.players[getIndexFromPosition(position)][index],
  );
  const {removePlayer, setIndicatorPosition, openDrawer, setPlayerStatsModal} = useLineupStore(
    (state) => ({
      removePlayer: state.removePlayer,
      setIndicatorPosition: state.setIndicatorPosition,
      openDrawer: state.openDrawer,
      setPlayerStatsModal: state.setPlayerStatsModal,
    }),
    shallow,
  );
  const competitionQuery = useCompetitionQuery(competitionId, {staleTime: 5 * 60 * 1000});
  const setPlayerUIState = useSetQueryPlayerUIState(competitionId);

  const isSelected = useLineupStore((state) =>
    isSubstitute
      ? state.indicatorPosition.substitute && state.indicatorPosition.position === position
      : !state.indicatorPosition.substitute &&
        state.indicatorPosition.position === position &&
        state.indicatorPosition.index === index,
  );

  const onPlayerClick = () => {
    setIndicatorPosition({index, position, substitute: isSubstitute});

    if (player) setPlayerStatsModal({context: 'lineup-player', action: 'remove', player, isSubstitute});
    else openDrawer();
  };

  const onActionClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();

    if (!player) {
      setIndicatorPosition({position, index, substitute: isSubstitute});
      return openDrawer();
    }

    removePlayer(player, isSubstitute);
    setPlayerUIState(`${player.team.id}`, player.position, player.id, 'remove');
  };

  return (
    <PlayerItem
      player={player}
      isSelected={!!isSelected}
      inLineup
      placeholderPosition={position}
      onPlayerClick={onPlayerClick}
      action={
        competitionQuery.data?.state === 'upcoming' && (
          <IconButton
            variant="unstyled"
            minW="unset"
            minH="unset"
            boxSize="9"
            bgColor="unset"
            icon={!player ? <AddIcon /> : <RemoveIcon />}
            title={!player ? t('fantasy.lineup.player.actions.add') : t('fantasy.lineup.player.actions.remove')}
            aria-label={!player ? t('fantasy.lineup.player.actions.add') : t('fantasy.lineup.player.actions.remove')}
            onClick={onActionClick}
          />
        )
      }
      _notLast={{mb: '2.5'}}
    />
  );
};
